import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Line,
  LineChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from 'recharts'
import { Link } from 'react-router-dom'
import { getAnalyticsOverview, type AnalyticsPeriodParams } from '../../shared/api/analytics.api'
import { Spinner } from '../../shared/ui/Spinner'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { STATUS_LABELS } from '../requests/components/requestsColDefs'
import type { RequestStatus } from '../../shared/api/types'
import { useAnalyticsTable, type ColumnDef } from './shared/useAnalyticsTable'
import { SortableTh } from './shared/SortableTh'

type StuckRow = {
  requestId: string
  requestNumber: string | null
  clientName: string
  status: string
  daysStuck: number
}

const statusLabel = (s: string): string => STATUS_LABELS[s as RequestStatus] ?? s

const STUCK_COLUMNS: ColumnDef<StuckRow>[] = [
  { key: 'requestNumber', sort: (r) => r.requestNumber ?? '' },
  { key: 'clientName', sort: (r) => r.clientName, filter: (r) => r.clientName },
  { key: 'status', sort: (r) => statusLabel(r.status), filter: (r) => statusLabel(r.status) },
  { key: 'daysStuck', sort: (r) => r.daysStuck },
]

/** Цвет ячейки тепловой карты: 0 → зелёный, 1 → красный. */
const heatColor = (ratio: number): string => `hsl(${Math.round(120 - ratio * 120)}, 68%, 52%)`

/** Дата для оси/тултипа: ISO `YYYY-MM-DD` → `ДД.ММ.ГГГГ`. */
const formatDay = (raw: unknown): string => {
  if (typeof raw !== 'string') return String(raw ?? '')
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : raw
}

/**
 * Вкладка «Обзор» модуля «Аналитика» (этап 3 docs/analytics-plan.md).
 * Виджеты: сквозная воронка, срыв дедлайнов, динамика по дням.
 */
export function OverviewTab({ periodParams }: { periodParams: AnalyticsPeriodParams }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['analytics-overview', periodParams],
    queryFn: () => getAnalyticsOverview(periodParams),
  })

  // Стадия с наибольшим падением конверсии относительно предыдущей — «затык».
  const bottleneckKey = useMemo(() => {
    if (!data) return null
    let worstKey: string | null = null
    let worstDrop = 0
    for (let i = 1; i < data.funnel.length; i++) {
      const drop = data.funnel[i - 1]!.conversion - data.funnel[i]!.conversion
      if (drop > worstDrop) {
        worstDrop = drop
        worstKey = data.funnel[i]!.key
      }
    }
    return worstKey
  }, [data])

  const stuckTable = useAnalyticsTable(
    (data?.stuck ?? []) as StuckRow[],
    STUCK_COLUMNS,
    { key: 'daysStuck', dir: 'desc' },
    'overview-stuck',
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    )
  }
  if (isError || !data) {
    return <ErrorMessage error="Не удалось загрузить аналитику обзора" />
  }

  return (
    <div className="space-y-4">
      {/* ── Сквозная воронка ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900">Сквозная воронка проекта</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Конверсия от созданных заявок. Красным — стадия с наибольшей потерей.
        </p>
        <div className="mt-4 space-y-2">
          {data.funnel.map((stage) => {
            const isBottleneck = stage.key === bottleneckKey
            return (
              <div key={stage.key} className="flex items-center gap-3">
                <div className="w-44 text-sm text-gray-700 flex-shrink-0">{stage.label}</div>
                <div className="flex-1 bg-gray-100 rounded-md h-7 relative overflow-hidden">
                  <div
                    className={`h-full rounded-md ${isBottleneck ? 'bg-red-400' : 'bg-brand-400'}`}
                    style={{ width: `${Math.max(stage.conversion, 1.5)}%` }}
                  />
                  <span className="absolute inset-y-0 left-2 flex items-center text-xs font-medium text-gray-700">
                    {stage.count.toLocaleString('ru-RU')}
                  </span>
                </div>
                <div className="w-16 text-right text-sm text-gray-500 flex-shrink-0">
                  {stage.conversion}%
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Заявки в отказ и в архив по причинам (19.09.2026, п. 19 встречи) ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <CloseReasonsCard
          title="Заявки в отказ"
          hint="Почему клиенты не купили за выбранный период"
          rows={data.closeReasons.rejection}
        />
        <CloseReasonsCard
          title="Заявки в архив"
          hint="Почему заявки ушли в архив за выбранный период"
          rows={data.closeReasons.archive}
        />
      </div>

      {/* ── Узкие места: среднее время стадий ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900">Узкие места — среднее время стадий</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Сколько дней в среднем заявка проходит каждую стадию. Красным — самая медленная.
        </p>
        <div className="mt-4 space-y-1.5">
          {data.stageTimes.map((st) => {
            const max = Math.max(...data.stageTimes.map((s) => s.avgDays), 0.1)
            const ratio = st.avgDays / max
            return (
              <div key={st.key} className="flex items-center gap-3">
                <div className="w-56 text-sm text-gray-700 flex-shrink-0">{st.label}</div>
                <div className="flex-1 bg-gray-100 rounded-md h-7 overflow-hidden">
                  <div
                    className="h-full rounded-md"
                    style={{
                      width: `${Math.max(ratio * 100, 2)}%`,
                      backgroundColor: heatColor(ratio),
                    }}
                  />
                </div>
                <div className="w-20 text-right text-sm text-gray-600 flex-shrink-0">
                  {st.avgDays} дн
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ── Срыв дедлайнов ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900">Срыв дедлайнов</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Клик по карточке открывает эти заявки списком.
        </p>
        <div className="mt-3 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <DeadlineCard
            label="Дедлайн прошёл, КП не отправлено"
            value={data.deadlines.overdueNoQuote}
            tone="red"
            to="/requests?filter=overdue"
          />
          <DeadlineCard
            label="из них застряло на продажах"
            value={data.deadlines.overdueOnSales}
            tone="amber"
            to="/requests?filter=overdue_sales"
          />
          <DeadlineCard
            label="из них застряло на закупке"
            value={data.deadlines.overdueOnProcurement}
            tone="amber"
            to="/requests?filter=overdue_procurement"
          />
          <DeadlineCard
            label="КП отправлено после дедлайна (справочно)"
            value={data.deadlines.lateQuote}
            tone="gray"
          />
        </div>
      </div>

      {/* ── Где сейчас застряло ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900">
          Где сейчас застряло
          {data.stuckTotal > 0 && (
            <span className="ml-2 text-xs font-normal text-gray-400">
              {data.stuckTotal.toLocaleString('ru-RU')}
            </span>
          )}
        </h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Активные заявки без движения дольше 14 дней (снимок на сейчас, не зависит от периода).
        </p>
        {data.stuckTotal > data.stuck.length && (
          <p className="mt-1 text-xs text-amber-600">
            Показаны первые {data.stuck.length} из {data.stuckTotal} — самые давние сверху.
          </p>
        )}
        {data.stuck.length === 0 ? (
          <p className="mt-3 text-sm text-gray-400">Застрявших заявок нет.</p>
        ) : (
          <div className="mt-3 max-h-[28rem] overflow-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 border-b border-gray-100 [&_th]:sticky [&_th]:top-0 [&_th]:bg-white [&_th]:z-10">
                  <SortableTh table={stuckTable} col="requestNumber" className="pr-3">
                    Заявка
                  </SortableTh>
                  <SortableTh table={stuckTable} col="clientName" className="pr-3">
                    Клиент
                  </SortableTh>
                  <SortableTh table={stuckTable} col="status" className="pr-3">
                    Статус
                  </SortableTh>
                  <SortableTh table={stuckTable} col="daysStuck" align="right">
                    Без движения
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {stuckTable.view.map((s) => (
                  <tr key={s.requestId} className="border-b border-gray-50">
                    <td className="py-1.5 pr-3">
                      <Link
                        to={`/requests/${s.requestId}`}
                        className="text-brand-600 hover:text-brand-700 font-medium"
                      >
                        {s.requestNumber ?? '—'}
                      </Link>
                    </td>
                    <td className="py-1.5 pr-3 text-gray-700">{s.clientName}</td>
                    <td className="py-1.5 pr-3 text-gray-600">{statusLabel(s.status)}</td>
                    <td className="py-1.5 text-right font-medium text-amber-600">
                      {s.daysStuck} дн
                    </td>
                  </tr>
                ))}
                {stuckTable.view.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-3 text-center text-sm text-gray-400">
                      Ничего не найдено по фильтру
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Динамика по дням ── */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900">Динамика по дням</h3>
        <div className="mt-3 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.timeline} margin={{ top: 8, right: 12, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDay}
                tick={{ fontSize: 10, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
                minTickGap={24}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#6b7280' }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
                allowDecimals={false}
              />
              <Tooltip
                labelFormatter={formatDay}
                contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 12 }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="created"
                name="Создано заявок"
                stroke="#3b82f6"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="toProcurement"
                name="В закупку"
                stroke="#a855f7"
                dot={false}
              />
              <Line
                type="monotone"
                dataKey="quotationCreated"
                name="КП создано"
                stroke="#f59e0b"
                dot={false}
              />
              <Line type="monotone" dataKey="ordered" name="В заказ" stroke="#16a34a" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

function DeadlineCard({
  label,
  value,
  tone,
  to,
}: {
  label: string
  value: number
  tone: 'red' | 'amber' | 'gray'
  /** Если задан — карточка кликабельна и ведёт в список заявок. */
  to?: string
}) {
  const toneCls =
    tone === 'red' ? 'text-red-600' : tone === 'amber' ? 'text-amber-600' : 'text-gray-700'
  const inner = (
    <>
      <div className={`text-2xl font-semibold ${toneCls}`}>{value.toLocaleString('ru-RU')}</div>
      <div className="text-xs text-gray-500 mt-1 leading-snug">{label}</div>
    </>
  )
  const cls = `block border border-gray-200 rounded-lg p-3 ${
    to ? 'hover:border-brand-300 hover:shadow-sm transition-colors cursor-pointer' : ''
  }`
  return to ? (
    <Link to={to} className={cls}>
      {inner}
    </Link>
  ) : (
    <div className={cls}>{inner}</div>
  )
}

/**
 * Причины отказа и архивации за период (19.09.2026). Раньше причина была
 * свободным текстом и в аналитику не попадала вовсе.
 */
function CloseReasonsCard({
  title,
  hint,
  rows,
}: {
  title: string
  hint: string
  rows: Array<{ code: string | null; label: string; count: number }>
}) {
  const total = rows.reduce((a, r) => a + r.count, 0)
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900">
        {title}
        <span className="ml-2 text-gray-400 font-normal">{total.toLocaleString('ru-RU')}</span>
      </h3>
      <p className="text-xs text-gray-500 mt-0.5">{hint}</p>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400 mt-4">Нет данных за период</p>
      ) : (
        <div className="mt-4 space-y-1.5">
          {rows.map((r) => {
            const share = total > 0 ? (r.count / total) * 100 : 0
            return (
              <div key={r.code ?? 'none'} className="flex items-center gap-3">
                <div className="w-56 text-sm text-gray-700 flex-shrink-0 truncate" title={r.label}>
                  {r.label}
                </div>
                <div className="flex-1 bg-gray-100 rounded-md h-6 overflow-hidden">
                  <div
                    className="h-full rounded-md bg-brand-500"
                    style={{ width: `${Math.max(share, 2)}%` }}
                  />
                </div>
                <div className="w-24 text-right text-sm tabular-nums text-gray-700">
                  {r.count} · {Math.round(share)}%
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
